import React, { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X, ChevronDown, Heart } from 'lucide-react';
import { LANGS } from '@/lib/i18n';
import { UKGMLogo } from '@/lib/ui';
import DonateModal from '@/components/DonateModal';

const linkClass = ({ isActive }) =>
  `text-sm font-medium transition-colors ${isActive ? 'text-ukgm-blue' : 'text-ukgm-slate hover:text-ukgm-blue'}`;

export default function PublicHeader() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [donateOpen, setDonateOpen] = useState(false);

  const items = [
    { to: '/', label: t('nav.home') },
    { to: '/about', label: t('nav.about') },
    { to: '/projects', label: t('nav.projects') },
    { to: '/blog', label: t('nav.blog') },
    { to: '/gallery', label: t('nav.gallery') },
    { to: '/transparency', label: t('nav.transparency') },
    { to: '/media', label: t('nav.media') },
    { to: '/contact', label: t('nav.contact') },
  ];

  const current = LANGS.find(l => l.code === i18n.language) || LANGS[0];

  return (
    <>
      <header className="sticky top-0 z-40 bg-ukgm-cream/90 backdrop-blur-xl border-b border-ukgm-gold/25" data-testid="public-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 md:h-20 flex items-center justify-between gap-6">
          <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
            <UKGMLogo className="w-11 h-11 md:w-12 md:h-12" />
            <div className="flex flex-col leading-tight">
              <span className="font-serif text-ukgm-blue font-semibold text-base md:text-lg tracking-tight">
                Universal Krishna Gaukul Mission
              </span>
              <span className="text-[10px] tracking-[0.24em] uppercase text-ukgm-gold font-semibold hidden sm:inline">
                A Registered Public Charitable Trust in India
              </span>
            </div>
          </Link>

          <nav className="hidden xl:flex items-center gap-6" data-testid="primary-nav">
            {items.map(i => (
              <NavLink key={i.to} to={i.to} className={linkClass} end={i.to === '/'} data-testid={`nav-${i.to.replace('/', '') || 'home'}`}>
                {i.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <div className="relative">
              <button
                onClick={() => setLangOpen(v => !v)}
                className="flex items-center gap-1 px-2 py-1.5 rounded-full text-xs font-semibold text-ukgm-blue border border-ukgm-gold/50 hover:bg-ukgm-goldLight"
                data-testid="language-switcher-btn"
              >
                {current.label} <ChevronDown className="w-3.5 h-3.5" />
              </button>
              {langOpen && (
                <div className="absolute right-0 mt-2 w-40 bg-white rounded-xl border border-ukgm-gold/30 shadow-xl py-1 z-50">
                  {LANGS.map(l => (
                    <button
                      key={l.code}
                      onClick={() => { i18n.changeLanguage(l.code); setLangOpen(false); document.documentElement.lang = l.code; }}
                      className={`w-full text-left px-4 py-2 text-sm flex items-center justify-between hover:bg-ukgm-goldLight ${i18n.language === l.code ? 'text-ukgm-blue font-semibold' : 'text-ukgm-slate'}`}
                      data-testid={`language-option-${l.code}`}
                    >
                      <span>{l.native}</span>
                      <span className="text-[10px] tracking-widest">{l.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={() => setDonateOpen(true)}
              className="hidden md:inline-flex items-center gap-2 bg-ukgm-gold text-ukgm-blue font-bold px-5 py-2.5 rounded-full text-sm hover:bg-ukgm-goldDark gold-glow"
              data-testid="donate-header-btn"
            >
              <Heart className="w-4 h-4" fill="currentColor" /> {t('nav.donate')}
            </button>

            <button onClick={() => setOpen(v => !v)} className="xl:hidden p-2 text-ukgm-blue" aria-label="menu" data-testid="mobile-menu-toggle">
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {open && (
          <div className="xl:hidden border-t border-ukgm-gold/20 bg-white">
            <div className="px-4 py-3 flex flex-col gap-1">
              {items.map(i => (
                <NavLink key={i.to} to={i.to} onClick={() => setOpen(false)}
                  className={({ isActive }) => `py-2.5 px-3 rounded-lg text-sm font-medium ${isActive ? 'bg-ukgm-blueLight text-ukgm-blue' : 'text-ukgm-slate'}`}
                  end={i.to === '/'}>{i.label}</NavLink>
              ))}
              <button onClick={() => { setDonateOpen(true); setOpen(false); }}
                className="mt-2 flex items-center justify-center gap-2 bg-ukgm-gold text-ukgm-blue font-bold px-4 py-3 rounded-full">
                <Heart className="w-4 h-4" fill="currentColor" /> {t('nav.donate')}
              </button>
            </div>
          </div>
        )}
      </header>

      <button onClick={() => setDonateOpen(true)}
        className="md:hidden fixed bottom-4 right-4 z-40 bg-ukgm-gold text-ukgm-blue font-bold h-12 pl-3 pr-4 rounded-full shadow-xl gold-glow flex items-center gap-2"
        data-testid="donate-fab">
        <Heart className="w-4 h-4" fill="currentColor" /> <span className="text-sm">{t('nav.donate')}</span>
      </button>

      <DonateModal open={donateOpen} onClose={() => setDonateOpen(false)} />
    </>
  );
}
